import { InteractionRequiredAuthError, InteractionType } from "@azure/msal-browser";
import { MsalAuthenticationTemplate, MsalProvider, useMsal } from "@azure/msal-react";
import { FC, useEffect, useState } from "react";
import publicClientApplication from "./msalPublicClientApplication";
import { TokenProvider, tokenProviderFactory } from "./tokenProvider";
import * as s from "./AuthenticatedApp.styles";
import { RCLoadingSpinner, ApplicationUser } from "@railcrew/design";
import { AuthConfig, storeAuthConfig } from "./authConfig";
import graphClient from "./graphClient";

export interface OnUserLoadedEvent {
  user?: ApplicationUser;
  error: boolean;
}

export interface AuthenticatedAppProps {
  configProvider: AuthConfig;
  apiScopes?: string[];
  graphScopes?: string[];
  onAuthenticated?(): void;
  onUserLoaded?(e: OnUserLoadedEvent): void;
  children: React.ReactNode
}

interface TokenVerifierProps {
  onTokenVerified(isVerified: boolean): void;
  configProvider: AuthConfig;
  apiScopes?: string[];
  graphScopes?: string[];
}

const ApiTokenVerifier: FC<TokenVerifierProps> = (props) => {
  const { instance } = useMsal();
  const { onTokenVerified } = props;

  useEffect(() => {
    (async () => {
      const runTokenVerification = async (provider: TokenProvider) => {
        try {
          await provider.getAccessToken();
        } catch (e) {
          if (e instanceof InteractionRequiredAuthError) {
            if ((props.configProvider.MSAL_LOGIN_METHOD || "").toLowerCase() === "popup") {
              instance.acquireTokenPopup(provider.getAccessTokenRequest());
            } else {
              instance.acquireTokenRedirect(provider.getAccessTokenRequest());
            }
          } else {
            throw e;
          }
        }
      };

      try {
        props.apiScopes && (await runTokenVerification(tokenProviderFactory(props.apiScopes)));
        props.graphScopes && (await runTokenVerification(tokenProviderFactory(props.graphScopes)));
        onTokenVerified(true);
      } catch (e) {
        onTokenVerified(false);
        console.log(e);
      }
    })();
  }, [onTokenVerified, instance, props.configProvider, props.apiScopes, props.graphScopes]);
  return <></>;
};

interface LoginRedirectVerifierProps {
  onLoginRedirectVerified(isVerified: boolean): void;
}
const LoginRedirectVerifier: FC<LoginRedirectVerifierProps> = (props) => {
  useEffect(() => {
    const msalInstance = publicClientApplication.instance();
    (async () => {
      await msalInstance.handleRedirectPromise();
      props.onLoginRedirectVerified(true);
    })();
  }, [props]);
  return <></>;
};

const AuthenticatedApp: FC<AuthenticatedAppProps> = (props) => {
  const [tokenVerified, setTokenVerified] = useState(false);
  const [loginRedirectVerified, setLoginRedirectVerified] = useState(false);
  const [configInjected, setConfigInjected] = useState(false);
  const [hasTriggeredAppInit, setHasTriggeredAppInit] = useState(false);
  const [user, setUser] = useState<ApplicationUser | undefined>();

  const { configProvider, onAuthenticated, onUserLoaded } = props;

  useEffect(() => {
    if (!configInjected) {
      storeAuthConfig(configProvider);
      setConfigInjected(true);
    }
  }, [setConfigInjected, configInjected, configProvider]);

  useEffect(() => {
    if (onAuthenticated && loginRedirectVerified && configInjected && tokenVerified) {
      !hasTriggeredAppInit && onAuthenticated();
      setHasTriggeredAppInit(true);
    }
  }, [configInjected, loginRedirectVerified, tokenVerified, onAuthenticated, hasTriggeredAppInit]);

  useEffect(() => {
    if (!user && hasTriggeredAppInit) {
      (async () => {
        try {
          const u = await graphClient.getUser();
          const mapped = { name: u.displayName || "", email: u.mail || "" };
          setUser(mapped);
          onUserLoaded && onUserLoaded({ user: mapped, error: false });
        } catch (e) {
          onUserLoaded && onUserLoaded({ error: true });
        }
      })();
    }
  }, [onUserLoaded, user, hasTriggeredAppInit]);

  if (!configInjected) {
    return null;
  }

  return (
    <MsalProvider instance={publicClientApplication.instance()}>
      <MsalAuthenticationTemplate
        interactionType={
          (props.configProvider.MSAL_LOGIN_METHOD || "").toLowerCase() === "popup"
            ? InteractionType.Popup
            : InteractionType.Redirect
        }
      >
        <ApiTokenVerifier onTokenVerified={setTokenVerified} {...props} />
        <LoginRedirectVerifier onLoginRedirectVerified={setLoginRedirectVerified} />
        {tokenVerified && loginRedirectVerified ? (
          props.children
        ) : (
          <s.App>
            <RCLoadingSpinner />
          </s.App>
        )}
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
};

export default AuthenticatedApp;
